import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import {
  Package,
  Camera,
  Truck,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Star,
  Search,
  Download,
  ShoppingBag,
  Send,
  Tag,
  Settings,
  LifeBuoy,
  ArrowUpRight,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { VerifiedStoreBadge } from "@/components/VerifiedBadges";
import { TrustedBadge } from "@/components/TrustedBadge";
import {
  getSellerHubOverview,
  listSellerHubOrders,
  getSellerHubPerformance,
  type SellerHubOrderRow,
  type SellerHubPerformance,
} from "@/lib/seller-hub.functions";
import { getMyProfile } from "@/lib/profile.functions";
import { SellerPayoutsCard } from "@/components/SellerPayoutsCard";
import { listMyPayouts, type PayoutRow } from "@/lib/payment-history.functions";

export const Route = createFileRoute("/_authenticated/seller-hub")({
  head: () => ({
    meta: [
      { title: "Seller Hub — Want To Buy" },
      { name: "description", content: "Manage your fulfillment offers, orders, and payouts." },
    ],
  }),
  component: SellerHubPage,
});

type Tab = "overview" | "orders" | "performance" | "payments" | "alerts";

function money(cents: number, currency = "USD") {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function SellerHubPage() {
  const [tab, setTab] = useState<Tab>("overview");
  const getProfile = useServerFn(getMyProfile);
  const { data: profile } = useQuery({
    queryKey: ["my-profile", "seller-hub"],
    queryFn: () => getProfile(),
    staleTime: 60_000,
  });

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "orders", label: "Orders" },
    { key: "performance", label: "Performance" },
    { key: "payments", label: "Payments" },
    { key: "alerts", label: "Price Alerts" },
  ];

  return (
    <AppShell>
      {/* Persistent seller header */}
      <div className="mb-6 rounded-xl border border-border bg-panel p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Seller Hub</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">
                @{profile?.username ?? "you"}
              </span>
              {profile?.seller_rating_count ? (
                <span className="inline-flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 fill-current text-yellow-500" />
                  {Number(profile.seller_rating_avg ?? 0).toFixed(2)} · {profile.seller_rating_count} ratings
                </span>
              ) : (
                <span>No ratings yet</span>
              )}
              {profile?.trusted_seller && <TrustedBadge role="seller" />}
              {profile?.verified_store && <VerifiedStoreBadge />}
            </div>
          </div>
        </div>

        {/* Tabs — desktop */}
        <div className="mt-5 hidden gap-1 border-b border-border sm:flex">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
                tab === t.key
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tabs — mobile dropdown */}
        <div className="mt-5 sm:hidden">
          <select
            value={tab}
            onChange={(e) => setTab(e.target.value as Tab)}
            className="h-10 w-full rounded-md border border-border bg-panel px-3 text-sm"
          >
            {tabs.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "performance" && <PerformanceTab />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "alerts" && <AlertsTab />}
    </AppShell>
  );
}

function OverviewTab() {
  const getOverview = useServerFn(getSellerHubOverview);
  const { data, isLoading } = useQuery({
    queryKey: ["seller-hub", "overview"],
    queryFn: () => getOverview(),
    staleTime: 30_000,
  });

  if (isLoading || !data) {
    return <div className="panel p-10 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  const { metrics, tasks, sales31d, earningsThisMonthCents, earningsLastMonthCents, avgOrderCents, feedback } = data;

  if (metrics.totalOffersSubmitted === 0) {
    return (
      <div className="panel p-10 text-center">
        <ShoppingBag className="mx-auto h-10 w-10 text-primary" />
        <h2 className="mt-3 font-display text-xl font-bold">Welcome to the Seller Hub</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Submit your first fulfillment offer to unlock your dashboard.
        </p>
        <Link to="/" className="btn-primary mt-4 inline-flex h-10 items-center gap-1.5 px-4 text-sm">
          Browse open buy orders
        </Link>
      </div>
    );
  }

  const pctChange =
    earningsLastMonthCents > 0
      ? Math.round(((earningsThisMonthCents - earningsLastMonthCents) / earningsLastMonthCents) * 100)
      : null;
  const maxSale = Math.max(1, ...sales31d.map((p) => p.cents));

  return (
    <div className="space-y-6">
      {/* Status bar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricCard icon={Package} label="Active Offers" value={metrics.activeOffers} tone="text-primary" />
        <MetricCard icon={Camera} label="Awaiting Photo Submission" value={metrics.awaitingPhotos} tone="text-amber-400" />
        <MetricCard icon={Truck} label="Awaiting Shipment" value={metrics.awaitingShipment} tone="text-blue-400" />
        <MetricCard icon={CheckCircle2} label="Completed This Month" value={metrics.completedThisMonth} tone="text-success" />
      </div>

      {/* Tasks */}
      <section className="panel p-5">
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <h2 className="font-display text-base font-bold">Tasks</h2>
        </div>
        {tasks.length === 0 ? (
          <div className="text-sm text-muted-foreground">All caught up — no urgent tasks right now.</div>
        ) : (
          <ul className="divide-y divide-border">
            {tasks.map((t) => (
              <li key={t.id}>
                <Link to={t.link as string} className="flex items-start gap-3 py-3 hover:bg-muted/40">
                  <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{t.title}</div>
                    <div className="text-xs text-muted-foreground">{t.body}</div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Sales chart */}
        <section className="panel col-span-2 p-5">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-display text-base font-bold">Sales — last 31 days</h2>
            <div className="text-xs text-muted-foreground">
              This month <span className="font-semibold text-foreground">{money(earningsThisMonthCents)}</span> ·
              {" "}Avg order <span className="font-semibold text-foreground">{money(avgOrderCents)}</span>
              {pctChange !== null && (
                <>
                  {" "}·{" "}
                  <span className={pctChange >= 0 ? "text-success" : "text-destructive"}>
                    {pctChange >= 0 ? "+" : ""}{pctChange}% vs last month
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex h-40 items-end gap-1">
            {sales31d.map((p) => (
              <div
                key={p.date}
                className="flex-1 rounded-t bg-primary/70"
                style={{ height: `${Math.max(2, (p.cents / maxSale) * 100)}%` }}
                title={`${p.date}: ${money(p.cents)}`}
              />
            ))}
          </div>
        </section>

        {/* Feedback + shortcuts */}
        <section className="space-y-6">
          <div className="panel p-5">
            <h2 className="mb-3 font-display text-base font-bold">Feedback</h2>
            <div className="flex items-baseline gap-2">
              <Star className="h-4 w-4 fill-current text-yellow-500" />
              <span className="text-2xl font-bold">
                {feedback.ratingCount > 0 ? feedback.ratingAvg.toFixed(2) : "—"}
              </span>
              <span className="text-xs text-muted-foreground">/ 5</span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {feedback.ratingCount} total · {feedback.recent30d} in last 30 days
            </div>
          </div>

          <div className="panel p-5">
            <h2 className="mb-3 font-display text-base font-bold">Shortcuts</h2>
            <div className="grid gap-2">
              <ShortcutLink to="/" icon={Search} label="Browse Open Orders" />
              <ShortcutLink to="/fulfillment-cart" icon={Send} label="Submit an Offer" />
              <ShortcutLink to="/my-listings" icon={Tag} label="My Sale Offers" />
              <ShortcutLink to="/account" icon={Settings} label="Account Settings" />
              <ShortcutLink to="/contact" icon={LifeBuoy} label="Contact Support" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Package;
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <div className="panel p-4">
      <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${tone}`}>
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
    </div>
  );
}

function ShortcutLink({ to, icon: Icon, label }: { to: string; icon: typeof Search; label: string }) {
  return (
    <Link
      to={to as string}
      className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="flex-1">{label}</span>
      <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
    </Link>
  );
}

const ORDER_FILTERS: { key: SellerHubOrderRow["bucket"] | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "pending_review", label: "Pending Review" },
  { key: "photo_submission", label: "Photo Submission" },
  { key: "awaiting_payment", label: "Awaiting Payment" },
  { key: "payment_confirmed", label: "Payment Confirmed" },
  { key: "shipped", label: "Shipped" },
  { key: "completed", label: "Completed" },
  { key: "withdrawn", label: "Withdrawn" },
];

const BUCKET_META: Record<
  SellerHubOrderRow["bucket"],
  { label: string; tone: string; action: string }
> = {
  pending_review: { label: "Pending buyer review", tone: "bg-muted text-muted-foreground", action: "View" },
  photo_submission: { label: "Awaiting buyer photo approval", tone: "bg-amber-500/10 text-amber-400", action: "View" },
  awaiting_payment: { label: "Awaiting buyer payment", tone: "bg-primary/10 text-primary", action: "View" },
  payment_confirmed: { label: "Payment confirmed — ship now", tone: "bg-blue-500/10 text-blue-300", action: "Mark as Shipped" },
  shipped: { label: "Shipped", tone: "bg-blue-500/10 text-blue-300", action: "View" },
  completed: { label: "Completed", tone: "bg-success/10 text-success", action: "Leave Rating" },
  withdrawn: { label: "Withdrawn / rejected", tone: "bg-muted text-muted-foreground", action: "View" },
};

function OrdersTab() {
  const navigate = useNavigate();
  const fetchOrders = useServerFn(listSellerHubOrders);
  const { data, isLoading } = useQuery({
    queryKey: ["seller-hub", "orders"],
    queryFn: () => fetchOrders(),
    staleTime: 30_000,
  });
  const [filter, setFilter] = useState<SellerHubOrderRow["bucket"] | "all">("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const orders = data?.orders ?? [];
  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (filter !== "all" && o.bucket !== filter) return false;
      if (q) {
        const s = q.toLowerCase();
        if (
          !(o.cardName ?? "").toLowerCase().includes(s) &&
          !(o.buyerUsername ?? "").toLowerCase().includes(s) &&
          !o.buyOrderId.toLowerCase().includes(s)
        ) return false;
      }
      if (from && o.updatedAt < from) return false;
      if (to && o.updatedAt > to + "T23:59:59") return false;
      return true;
    });
  }, [orders, filter, q, from, to]);

  function downloadCsv() {
    const header = [
      "OfferID","BuyOrderID","Card","Buyer","BuyerRating","Price","Shipping","Currency","Status","UpdatedAt",
    ];
    const rows = filtered.map((o) => [
      o.offerId, o.buyOrderId, o.cardName ?? "", o.buyerUsername ?? "", o.buyerRatingAvg ?? "",
      (o.priceCents / 100).toFixed(2), (o.shippingCents / 100).toFixed(2), o.currency,
      BUCKET_META[o.bucket].label, o.updatedAt,
    ]);
    const csv = [header, ...rows].map((r) =>
      r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `seller-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1 border-b border-border">
        {ORDER_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium transition ${
              filter === f.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search card, buyer, or order id"
            className="h-9 w-full rounded-md border border-border bg-panel pl-8 pr-3 text-sm"
          />
        </div>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-md border border-border bg-panel px-2 text-sm" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-md border border-border bg-panel px-2 text-sm" />
        <button onClick={downloadCsv} className="btn-primary inline-flex h-9 items-center gap-1.5 px-3 text-xs">
          <Download className="h-3.5 w-3.5" /> Download Report
        </button>
      </div>

      {isLoading ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground">No orders match.</div>
      ) : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-3">Card</th>
                <th className="p-3">Buyer</th>
                <th className="p-3">Price</th>
                <th className="p-3">Status</th>
                <th className="p-3">Updated</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const meta = BUCKET_META[o.bucket];
                return (
                  <tr key={o.offerId} className="border-b border-border/50 hover:bg-muted/40">
                    <td className="p-3">
                      <Link to="/orders/$id" params={{ id: o.buyOrderId }} className="font-semibold hover:underline">
                        {o.cardName ?? "Buy order"}
                      </Link>
                      <div className="text-[10px] uppercase text-muted-foreground">#{o.buyOrderId.slice(0, 8)}</div>
                    </td>
                    <td className="p-3">
                      {o.buyerUsername ? (
                        <span>@{o.buyerUsername}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {o.buyerRatingAvg != null && o.buyerRatingAvg > 0 && (
                        <div className="text-[10px] text-muted-foreground">
                          ★ {o.buyerRatingAvg.toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-mono">
                      {money(o.priceCents, o.currency)}
                      {o.shippingCents > 0 && (
                        <div className="text-[10px] text-muted-foreground">
                          + {money(o.shippingCents, o.currency)} ship
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${meta.tone}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {new Date(o.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => navigate({ to: "/orders/$id", params: { id: o.buyOrderId } })}
                        className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
                      >
                        {meta.action}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ComingSoon({ label }: { label: string }) {
  return (
    <div className="panel p-10 text-center">
      <h2 className="font-display text-xl font-bold">{label}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Coming in the next phase of the Seller Hub build-out.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Performance tab
// ─────────────────────────────────────────────────────────────

function PerformanceTab() {
  const fetchPerf = useServerFn(getSellerHubPerformance);
  const { data, isLoading } = useQuery({
    queryKey: ["seller-hub", "performance"],
    queryFn: () => fetchPerf(),
    staleTime: 60_000,
  });

  if (isLoading || !data) {
    return <div className="panel p-10 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  const p: SellerHubPerformance = data;
  const totalRatings = p.ratings.total;
  const rows: Array<[1 | 2 | 3 | 4 | 5, number]> = [
    [5, p.ratings.breakdown[5]],
    [4, p.ratings.breakdown[4]],
    [3, p.ratings.breakdown[3]],
    [2, p.ratings.breakdown[2]],
    [1, p.ratings.breakdown[1]],
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Acceptance rate"
          value={`${p.acceptanceRatePct}%`}
          benchmark={`${p.benchmark.acceptanceRatePct}% platform avg`}
          higherIsBetter
          you={p.acceptanceRatePct}
          bench={p.benchmark.acceptanceRatePct}
          sub={`${p.totalAccepted} of ${p.totalOffers} offers accepted`}
        />
        <StatCard
          label="Avg photo submission time"
          value={p.avgPhotoHours != null ? `${p.avgPhotoHours}h` : "—"}
          benchmark={`${p.benchmark.avgPhotoHours}h platform avg`}
          higherIsBetter={false}
          you={p.avgPhotoHours ?? undefined}
          bench={p.benchmark.avgPhotoHours}
          sub="Offer accepted → photos uploaded"
        />
        <StatCard
          label="Avg shipping time"
          value={p.avgShipHours != null ? `${p.avgShipHours}h` : "—"}
          benchmark={`${p.benchmark.avgShipHours}h platform avg`}
          higherIsBetter={false}
          you={p.avgShipHours ?? undefined}
          bench={p.benchmark.avgShipHours}
          sub="Payment received → marked shipped"
        />
        <StatCard
          label="Dispute rate"
          value={`${p.disputeRatePct}%`}
          benchmark={`${p.benchmark.disputeRatePct}% platform avg`}
          higherIsBetter={false}
          you={p.disputeRatePct}
          bench={p.benchmark.disputeRatePct}
          sub={`${p.totalDisputes} disputes of ${p.totalCompleted} orders`}
        />
      </div>

      <section className="panel p-5">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-base font-bold">Ratings breakdown</h2>
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">
              {totalRatings > 0 ? p.ratings.avg.toFixed(2) : "—"}
            </span>{" "}
            avg · {totalRatings} total · platform avg{" "}
            <span className="font-semibold text-foreground">{p.benchmark.ratingAvg}</span>
          </div>
        </div>
        {totalRatings === 0 ? (
          <div className="text-sm text-muted-foreground">No ratings yet.</div>
        ) : (
          <div className="space-y-1.5">
            {rows.map(([score, count]) => {
              const pct = Math.round((count / totalRatings) * 100);
              return (
                <div key={score} className="flex items-center gap-2 text-xs">
                  <span className="w-8 tabular-nums text-muted-foreground">{score}★</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-16 text-right tabular-nums text-muted-foreground">
                    {count} ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <p className="text-center text-xs text-muted-foreground">
        Benchmarks reflect current platform-wide averages. Consistently outperforming benchmarks improves
        your standing for the Trusted Seller program.
      </p>
    </div>
  );
}

function StatCard({
  label,
  value,
  benchmark,
  higherIsBetter,
  you,
  bench,
  sub,
}: {
  label: string;
  value: string;
  benchmark: string;
  higherIsBetter: boolean;
  you?: number;
  bench: number;
  sub?: string;
}) {
  let tone = "text-muted-foreground";
  let indicator = "—";
  if (you != null) {
    const better = higherIsBetter ? you >= bench : you <= bench;
    tone = better ? "text-success" : "text-amber-500";
    indicator = better ? "Above avg" : "Below avg";
  }
  return (
    <div className="panel p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      <div className={`mt-1 text-[11px] font-semibold ${tone}`}>{indicator}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{benchmark}</div>
      {sub && <div className="mt-2 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Payments tab (seller-focused)
// ─────────────────────────────────────────────────────────────

function PaymentsTab() {
  const [payouts, setPayouts] = useState<PayoutRow[] | null>(null);
  useEffect(() => {
    listMyPayouts().then(setPayouts).catch(() => setPayouts([]));
  }, []);

  const totalNet = (payouts ?? []).reduce((s, p) => s + p.net_cents, 0);
  const totalFees = (payouts ?? []).reduce((s, p) => s + p.fee_cents, 0);

  return (
    <div className="space-y-6">
      <section className="panel p-5">
        <h2 className="font-display text-base font-bold">Payout connection</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Buyers pay you directly via PayPal Goods &amp; Services. Want To Buy does not hold or
          release your funds — money lands in your PayPal account at checkout, minus the platform
          partner fee.
        </p>
        <div className="mt-4">
          <SellerPayoutsCard />
        </div>
      </section>

      <section className="panel p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-base font-bold">Payout history</h2>
          <div className="text-xs text-muted-foreground">
            Total net{" "}
            <span className="font-semibold text-foreground">{money(totalNet)}</span> · fees{" "}
            <span className="font-semibold text-foreground">{money(totalFees)}</span>
          </div>
        </div>

        {payouts === null ? (
          <div className="mt-4 text-sm text-muted-foreground">Loading…</div>
        ) : payouts.length === 0 ? (
          <div className="mt-4 text-sm text-muted-foreground">No payouts yet.</div>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2">Date</th>
                <th>Order</th>
                <th className="text-right">Gross</th>
                <th className="text-right">Fee</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">
                    {new Date(p.released_at).toLocaleDateString()}
                  </td>
                  <td className="truncate">{p.card_name ?? "—"}</td>
                  <td className="text-right font-mono">{money(p.gross_cents, p.currency)}</td>
                  <td className="text-right text-destructive">
                    -{money(p.fee_cents, p.currency)}
                  </td>
                  <td className="text-right font-mono font-semibold">
                    {money(p.net_cents, p.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="text-center text-xs text-muted-foreground">
        Payments flow directly buyer → seller via PayPal Goods &amp; Services. Want To Buy never
        holds, controls, or stores payment details.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Price Alerts tab — full manager (create / edit / pause / delete)
// ─────────────────────────────────────────────────────────────

function AlertsTab() {
  return <PriceAlertsManager />;
}

import { useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, BellRing, BellOff, Save, X } from "lucide-react";
import { toast } from "sonner";
import {
  listMyPriceAlerts,
  createPriceAlert,
  updatePriceAlert,
  togglePriceAlert,
  deletePriceAlert,
  TCG_ENUM_VALUES,
  TCG_LABELS,
  type PriceAlertRow,
  type TcgEnum,
} from "@/lib/price-alerts.functions";

type DraftAlert = {
  card_name: string;
  tcg: TcgEnum;
  set_name: string;
  target_price: string; // dollars as string in the input
  currency: string;
};

const EMPTY_DRAFT: DraftAlert = {
  card_name: "",
  tcg: "pokemon",
  set_name: "",
  target_price: "",
  currency: "USD",
};

function toCents(dollars: string): number {
  const n = Number(dollars);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
}

function PriceAlertsManager() {
  const qc = useQueryClient();
  const listFn = useServerFn(listMyPriceAlerts);
  const createFn = useServerFn(createPriceAlert);
  const updateFn = useServerFn(updatePriceAlert);
  const toggleFn = useServerFn(togglePriceAlert);
  const deleteFn = useServerFn(deletePriceAlert);

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ["price-alerts", "mine"],
    queryFn: () => listFn(),
    staleTime: 30_000,
  });

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<DraftAlert>(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftAlert>(EMPTY_DRAFT);
  const [busy, setBusy] = useState<string | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["price-alerts", "mine"] });

  async function handleCreate() {
    const cents = toCents(draft.target_price);
    if (!draft.card_name.trim() || cents <= 0) {
      toast.error("Card name and a target price above 0 are required");
      return;
    }
    setBusy("create");
    try {
      await createFn({
        data: {
          card_name: draft.card_name.trim(),
          tcg: draft.tcg,
          set_name: draft.set_name.trim() || null,
          target_price_cents: cents,
          currency: draft.currency || "USD",
        },
      });
      toast.success("Alert created");
      setDraft(EMPTY_DRAFT);
      setShowForm(false);
      await invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create alert");
    } finally {
      setBusy(null);
    }
  }

  function startEdit(a: PriceAlertRow) {
    setEditingId(a.id);
    setEditDraft({
      card_name: a.card_name,
      tcg: a.tcg,
      set_name: a.set_name ?? "",
      target_price: (a.target_price_cents / 100).toFixed(2),
      currency: a.currency,
    });
  }

  async function saveEdit(id: string) {
    const cents = toCents(editDraft.target_price);
    if (!editDraft.card_name.trim() || cents <= 0) {
      toast.error("Card name and a target price above 0 are required");
      return;
    }
    setBusy(id);
    try {
      await updateFn({
        data: {
          id,
          card_name: editDraft.card_name.trim(),
          tcg: editDraft.tcg,
          set_name: editDraft.set_name.trim() || null,
          target_price_cents: cents,
          currency: editDraft.currency || "USD",
        },
      });
      toast.success("Alert updated");
      setEditingId(null);
      await invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setBusy(null);
    }
  }

  async function handleToggle(a: PriceAlertRow) {
    setBusy(a.id);
    try {
      await toggleFn({ data: { id: a.id, active: !a.active } });
      await invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this price alert?")) return;
    setBusy(id);
    try {
      await deleteFn({ data: { id } });
      toast.success("Alert deleted");
      await invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <section className="panel p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-base font-bold flex items-center gap-2">
              <BellRing className="h-4 w-4 text-primary" /> Price alerts
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Get notified when a buyer posts a matching order at or above your target price.
              Create as many alerts as you need; pause any anytime.
            </p>
          </div>
          <button
            onClick={() => {
              setShowForm((s) => !s);
              setDraft(EMPTY_DRAFT);
            }}
            className="btn-primary inline-flex items-center gap-1.5 px-3.5 py-2 text-sm"
          >
            {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {showForm ? "Cancel" : "New alert"}
          </button>
        </div>

        {showForm && (
          <div className="mt-4 grid gap-3 rounded-md border border-border bg-background/60 p-4 sm:grid-cols-2">
            <AlertField label="Card name">
              <input
                className="alert-input"
                placeholder="e.g. Charizard VMAX Alt Art"
                value={draft.card_name}
                onChange={(e) => setDraft({ ...draft, card_name: e.target.value })}
              />
            </AlertField>
            <AlertField label="TCG">
              <select
                className="alert-input"
                value={draft.tcg}
                onChange={(e) => setDraft({ ...draft, tcg: e.target.value as TcgEnum })}
              >
                {TCG_ENUM_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {TCG_LABELS[v]}
                  </option>
                ))}
              </select>
            </AlertField>
            <AlertField label="Set (optional)">
              <input
                className="alert-input"
                placeholder="e.g. Evolving Skies"
                value={draft.set_name}
                onChange={(e) => setDraft({ ...draft, set_name: e.target.value })}
              />
            </AlertField>
            <AlertField label={`Target price (${draft.currency})`}>
              <input
                type="number"
                min={0}
                step="0.01"
                className="alert-input"
                placeholder="0.00"
                value={draft.target_price}
                onChange={(e) => setDraft({ ...draft, target_price: e.target.value })}
              />
            </AlertField>
            <AlertField label="Currency">
              <select
                className="alert-input"
                value={draft.currency}
                onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              >
                <option value="USD">USD</option>
                <option value="CAD">CAD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </AlertField>
            <div className="sm:col-span-2 flex justify-end">
              <button
                onClick={handleCreate}
                disabled={busy === "create"}
                className="btn-primary inline-flex items-center gap-1.5 px-3.5 py-2 text-sm disabled:opacity-60"
              >
                <Save className="h-4 w-4" /> Save alert
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="panel p-2">
        {isLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading alerts…</div>
        ) : alerts.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No price alerts yet — create one to be notified when matching buy orders are posted.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {alerts.map((a) => {
              const isEditing = editingId === a.id;
              return (
                <li key={a.id} className="p-3 sm:p-4">
                  {isEditing ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <AlertField label="Card name">
                        <input
                          className="alert-input"
                          value={editDraft.card_name}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, card_name: e.target.value })
                          }
                        />
                      </AlertField>
                      <AlertField label="TCG">
                        <select
                          className="alert-input"
                          value={editDraft.tcg}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, tcg: e.target.value as TcgEnum })
                          }
                        >
                          {TCG_ENUM_VALUES.map((v) => (
                            <option key={v} value={v}>
                              {TCG_LABELS[v]}
                            </option>
                          ))}
                        </select>
                      </AlertField>
                      <AlertField label="Set (optional)">
                        <input
                          className="alert-input"
                          value={editDraft.set_name}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, set_name: e.target.value })
                          }
                        />
                      </AlertField>
                      <AlertField label={`Target price (${editDraft.currency})`}>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          className="alert-input"
                          value={editDraft.target_price}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, target_price: e.target.value })
                          }
                        />
                      </AlertField>
                      <AlertField label="Currency">
                        <select
                          className="alert-input"
                          value={editDraft.currency}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, currency: e.target.value })
                          }
                        >
                          <option value="USD">USD</option>
                          <option value="CAD">CAD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </AlertField>
                      <div className="sm:col-span-2 flex justify-end gap-2">
                        <button
                          onClick={() => setEditingId(null)}
                          className="btn-ghost px-3 py-1.5 text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => saveEdit(a.id)}
                          disabled={busy === a.id}
                          className="btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-sm disabled:opacity-60"
                        >
                          <Save className="h-4 w-4" /> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-display text-base font-bold truncate">
                            {a.card_name}
                          </div>
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                              a.active
                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                : "border-border bg-muted/40 text-muted-foreground"
                            }`}
                          >
                            {a.active ? "Active" : "Paused"}
                          </span>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {TCG_LABELS[a.tcg]}
                          {a.set_name ? ` · ${a.set_name}` : ""} · Threshold{" "}
                          <span className="font-semibold text-foreground">
                            {money(a.target_price_cents, a.currency)}
                          </span>
                          {a.last_triggered_at && (
                            <>
                              {" · "}
                              Last match{" "}
                              {new Date(a.last_triggered_at).toLocaleDateString()}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleToggle(a)}
                          disabled={busy === a.id}
                          title={a.active ? "Pause alert" : "Resume alert"}
                          className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground hover:text-foreground disabled:opacity-50"
                        >
                          {a.active ? (
                            <BellOff className="h-3.5 w-3.5" />
                          ) : (
                            <BellRing className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => startEdit(a)}
                          title="Edit"
                          className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground hover:text-foreground"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(a.id)}
                          disabled={busy === a.id}
                          title="Delete"
                          className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground hover:text-destructive disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <style>{`.alert-input { width:100%; height:2.25rem; border-radius:0.375rem; border:1px solid var(--color-border); background: var(--color-background); padding: 0 0.625rem; font-size: 0.875rem; color: var(--color-foreground); }
      .alert-input:focus { outline:none; border-color: var(--color-primary); }`}</style>
    </div>
  );
}

function AlertField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
